import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hidden", "results", "list"]
  static values = {
    url: String,
    emptyMessage: { type: String, default: "No results found" },
    createLabel: { type: String, default: "" }
  }

  connect() {
    this._onClickOutside = (e) => {
      if (!this.element.contains(e.target)) this._hideResults()
    }
    document.addEventListener("click", this._onClickOutside)
    this._selectedIndex = -1
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    clearTimeout(this._debounce)
  }

  onInput() {
    const query = this.inputTarget.value.trim()

    // Clear hidden field when user edits the text
    this.hiddenTarget.value = ""

    if (query.length < 3) {
      this._hideResults()
      return
    }

    clearTimeout(this._debounce)
    this._debounce = setTimeout(() => this._search(query), 300)
  }

  onKeydown(event) {
    if (!this.hasResultsTarget || this.resultsTarget.classList.contains("hidden")) return

    const items = this.resultsTarget.querySelectorAll("[data-autocomplete-item]")
    if (items.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this._selectedIndex = Math.min(this._selectedIndex + 1, items.length - 1)
        this._highlightItem(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this._selectedIndex = Math.max(this._selectedIndex - 1, 0)
        this._highlightItem(items)
        break
      case "Enter":
        event.preventDefault()
        if (this._selectedIndex >= 0 && items[this._selectedIndex]) {
          items[this._selectedIndex].click()
        }
        break
      case "Escape":
        this._hideResults()
        break
    }
  }

  select(event) {
    const item = event.currentTarget
    const id = item.dataset.id
    const name = item.dataset.name

    this.inputTarget.value = name
    this.hiddenTarget.value = id
    this._hideResults()

    // Dispatch custom event with full data payload for listeners
    const detail = { id, name }
    // Copy all extra data-* attributes into the detail
    for (const key of Object.keys(item.dataset)) {
      if (key !== "id" && key !== "name" && key !== "autocompleteItem" && key !== "action") {
        detail[key] = item.dataset[key]
      }
    }
    this.dispatch("selected", { detail })
  }

  createAction() {
    const query = this.inputTarget.value.trim()
    this._hideResults()
    this.dispatch("create", { detail: { query } })
  }

  async _search(query) {
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
      const response = await fetch(url, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) return

      const results = await response.json()
      this._renderResults(results)
    } catch {
      this._hideResults()
    }
  }

  _renderResults(results) {
    this._selectedIndex = -1

    // Write into the list target (scrollable area), or fall back to results target
    const listEl = this.hasListTarget ? this.listTarget : this.resultsTarget

    if (results.length === 0) {
      listEl.innerHTML = `<div class="px-3 py-2 text-xs text-gray-400">${this._esc(this.emptyMessageValue)}</div>`
    } else {
      listEl.innerHTML = results.map(r => {
        // Build extra data attributes from all keys beyond id/name
        const extras = Object.keys(r)
          .filter(k => k !== "id" && k !== "name")
          .map(k => `data-${k}="${this._esc(String(r[k] ?? ""))}"`)
          .join(" ")

        return `<div data-autocomplete-item data-id="${this._esc(String(r.id))}" data-name="${this._esc(r.name)}"
              ${extras}
              data-action="click->autocomplete#select"
              class="px-3 py-1.5 text-sm cursor-pointer hover:bg-brand/10 hover:text-brand transition-colors">${this._esc(r.name)}</div>`
      }).join("")
    }

    this.resultsTarget.classList.remove("hidden")
    this._positionResults()
  }

  _positionResults() {
    if (!this.hasInputTarget || !this.hasResultsTarget) return
    if (getComputedStyle(this.resultsTarget).position !== "fixed") return
    const rect = this.inputTarget.getBoundingClientRect()
    const results = this.resultsTarget
    results.style.top = `${rect.bottom + 4}px`
    results.style.left = `${rect.left}px`
    results.style.width = `${rect.width}px`
  }

  _highlightItem(items) {
    items.forEach((item, i) => {
      if (i === this._selectedIndex) {
        item.classList.add("bg-brand/10", "text-brand")
      } else {
        item.classList.remove("bg-brand/10", "text-brand")
      }
    })
  }

  _hideResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden")
      if (this.hasListTarget) {
        this.listTarget.innerHTML = ""
      } else {
        this.resultsTarget.innerHTML = ""
      }
    }
    this._selectedIndex = -1
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
